/* The Woolshire rebrand 2026: the "Find your fill" quiz, a dialog opened by any [data-ws-quiz-open] link.
   Markup: a <template> and a JSON block rendered by snippets/woolshire-fill-quiz.liquid, either inside
   sections/woolshire-pdp-main.liquid (PDP mode: the "Find your fill" link in the fill chooser's legend)
   or, off the product page, by a section that carries a trigger (standalone mode: the fill guide on the
   homepage and Shop page, the Why wool page's pillow block). One dialog per page however many triggers
   or templates there are: the PDP's template wins, else the first standalone one. On the first open the
   template is cloned into a dialog appended to <body> (outside any transformed ancestor), so the page
   itself gains no headings, no text and no render work. Without JavaScript every trigger is a plain
   link to the full guide.
   Logic: the live quiz in /blogs/blog/how-to-choose-the-right-pillow (pillow-quiz/embed.html), kept
   exactly: Medium is the home base, netSignal = mattress + frame, the complaint nudge, "packs down"
   is a durability answer, and the borderline "we lean Medium" note.
   Add to cart (PDP): selects the fill in the page's own fill chooser, then hands the page's real product
   form to the cart drawer (window.cartDrawer.addFromForm), so the price, photo, address and the
   drawer all follow the same path as a tap on the fill row. Standalone: a one-line form with the
   recommended variant id goes through the same window.cartDrawer.addFromForm (/cart/add.js, then the
   drawer re-renders, opens and the header count updates); "Choose this fill" goes to the product page
   with ?variant=<id>#fill, which the PDP pre-selects server-side. A sold-out fill is never added.
   Vanilla JS, no dependencies. */
(function () {
  'use strict';
  // The snippet may be rendered by more than one section on a page: run once.
  if (window.__wsFillQuizLoaded) return;
  window.__wsFillQuizLoaded = true;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var motion = function () { return !(reduce && reduce.matches); };
  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  /* ---------------------------------------------------------------- the logic (live quiz, verbatim) */
  var TIERS = [
    { key: 'thin',       name: 'Thin',       wool: 'about 1½ lb of wool', detail: 'A low, flat loft.' },
    { key: 'medium',     name: 'Medium',     wool: 'about 2 lb of wool',       detail: 'A supportive, middle loft.' },
    { key: 'full',       name: 'Full',       wool: 'about 2½ lb of wool', detail: 'A taller loft that fills the gap between shoulder and ear.' },
    { key: 'extra-full', name: 'Extra Full', wool: 'about 3 lb of wool',       detail: 'Our firmest pillow, with compressed wool to resist settling.' }
  ];
  var STEPS = ['position', 'mattress', 'complaint', 'frame'];

  // Medium is the home base. For side sleepers, loft rises only with a clear upward signal
  // (firm mattress and/or broad frame); a soft mattress or smaller frame keeps them at Medium.
  // Frame = shoulder width; the build-question subtext anchors self-reporting.
  function netSignal(a) {
    var m = ({ soft: -1, medium: 0, firm: 1 })[a.mattress] || 0;
    var f = ({ petite: -1, average: 0, broad: 1 })[a.frame] || 0;
    return m + f;
  }

  function calcTier(a) {
    var idx = 1;      // Medium default
    var floor = 0;
    if (a.position === 'stomach') {
      idx = 0;                                   // Thin, always
    } else if (a.position === 'back') {
      idx = 1; floor = 1;                        // Medium
    } else if (a.position === 'combo') {
      idx = 1; floor = 1;
      if (netSignal(a) >= 2) idx = 2;            // broad + firm combo -> Full
    } else { // side
      floor = 1;                                 // a side sleeper never drops below Medium
      var net = netSignal(a);
      if (net < 1) idx = 1;                      // Medium - the majority (soft mattress / smaller frame)
      else if (net < 2) idx = 2;                 // Full - one clear upward signal (firm OR broad)
      else idx = 3;                              // Extra Full - broad AND firm
    }
    if (a.complaint === 'flat') idx += 1;        // too flat -> need more
    else if (a.complaint === 'high') idx -= 1;   // too high -> need less
    // "packs down" is a durability answer, not a reason to add height: only nudge a side
    // sleeper already at Full to Extra Full (pre-compressed), and never on a soft mattress.
    if (a.complaint === 'packs_down' && a.position === 'side' && idx === 2 && a.mattress !== 'soft') idx = 3;
    return Math.max(floor, Math.min(3, idx));
  }

  function isBorderlineMedium(a, idx) {
    return a.position === 'side' && idx === 1 && (a.frame === 'broad' || a.mattress === 'firm');
  }

  function buildReason(a, idx) {
    var positionLine = {
      side:    'Side sleepers need enough loft to fill the gap between shoulder and ear so the spine stays neutral, but how much depends on your mattress and frame.',
      back:    'Back sleepers do best with a medium loft. Enough to support the natural curve of the neck without pushing the head forward.',
      stomach: 'Stomach sleepers need a low loft. Anything taller arches the neck back and causes morning stiffness.',
      combo:   "Combination sleepers do best with a middle-of-the-road loft that works whether you're on your back or side."
    }[a.position] || '';

    var mods = [];
    // Frame: only claim a "bigger gap" when we actually sized up.
    if (a.frame === 'broad' && a.position === 'side' && idx >= 2) {
      mods.push('your broader shoulders open a bigger gap to fill');
    } else if (a.frame === 'petite' && a.position !== 'stomach') {
      mods.push('your smaller frame means less loft is needed');
    }
    // Mattress: kept consistent with the actual recommendation.
    if (a.mattress === 'soft' && a.position === 'side') {
      mods.push("your soft mattress lets your shoulder sink in, so you need less loft than a side sleeper usually would");
    } else if (a.mattress === 'firm' && a.position === 'side' && idx >= 2) {
      mods.push("your firm mattress doesn't let your shoulder sink, so you need the extra height");
    } else if (a.mattress === 'firm' && a.position === 'side') {
      mods.push("even on a firmer mattress, your shoulder width keeps you comfortably in the Medium range");
    }
    if (a.complaint === 'flat') {
      mods.push('your current pillow runs flat, so we nudged you up a level');
    } else if (a.complaint === 'high') {
      mods.push("your current pillow feels too high, so we eased back a level");
    } else if (a.complaint === 'packs_down' && idx === 3) {
      mods.push("your current pillow packs down overnight. Extra Full uses pre-compressed wool, so it holds its loft");
    } else if (a.complaint === 'packs_down') {
      mods.push("you mentioned your current pillow packs down. Wool holds loft far better than down or polyester, so there's no need to size up for it");
    } else if (a.complaint === 'hot') {
      mods.push('you mentioned overheating. Wool wicks moisture and breathes much better than down or memory foam');
    }
    var out = positionLine;
    if (mods.length) out += ' Based on your answers, ' + mods.join('; and ') + '.';
    return out;
  }

  // The live quiz appends this to the reason; here it is its own paragraph, same words.
  function borderlineNote(a, idx) {
    if (!isBorderlineMedium(a, idx)) return '';
    return "This one's a close call between Medium and Full, and when it's close we recommend Medium on purpose. Too much loft strains your neck; too little is an easy fix. If your current pillow has ever felt too flat on your side, size up to Full, and either way you get one free fill adjustment.";
  }

  /* ---------------------------------------------------------------- the dialog */
  function Quiz(sectionId) {
    this.sectionId = sectionId;
    this.tpl = $('[data-ws-quiz-template="' + sectionId + '"]');
    // standalone (no fill chooser on the page): the result adds by variant id and links to the product page
    this.standalone = !!(this.tpl && this.tpl.hasAttribute('data-ws-quiz-standalone'));
    this.pdp = this.standalone ? null : $('[data-ws-pdp][data-section-id="' + sectionId + '"]');
    var dataEl = $('[data-ws-quiz-data="' + sectionId + '"]');
    this.data = { fills: [], guideUrl: '' };
    try { if (dataEl) this.data = JSON.parse(dataEl.textContent); } catch (_) {}
    this.css = $('link[data-ws-quiz-css]');
    this.answers = {};
    this.step = 0;
    this.view = 'step';
    this.started = false;
    this.root = null;
    this.advancing = false;
  }

  Quiz.prototype.build = function () {
    if (this.root || !this.tpl) return;
    var frag = document.importNode(this.tpl.content, true);
    var root = frag.firstElementChild;
    document.body.appendChild(root);
    this.root = root;
    this.panel = $('.ws-quiz__panel', root);
    this.body = $('.ws-quiz__body', root);
    this.steps = $$('[data-ws-quiz-step]', root);
    this.result = $('[data-ws-quiz-result]', root);
    this.bars = $$('.ws-quiz__bars i', root);
    this.count = $('[data-ws-quiz-progress-text]', root);
    this.nav = $('.ws-quiz__nav', root);
    this.back = $('[data-ws-quiz-back]', root);
    this.next = $('[data-ws-quiz-next]', root);
    var self = this;

    root.addEventListener('click', function (e) {
      var t = e.target;
      var opt = t.closest && t.closest('[data-ws-quiz-answer]');
      if (opt) { self.answer(opt); return; }
      if (t.closest('[data-ws-quiz-close]') || t.closest('[data-ws-quiz-backdrop]')) { self.close(); return; }
      if (t.closest('[data-ws-quiz-back]')) { self.show(Math.max(0, self.step - 1)); return; }
      if (t.closest('[data-ws-quiz-next]')) { self.forward(); return; }
      if (t.closest('[data-ws-quiz-restart]')) { self.restart(); return; }
      if (t.closest('[data-ws-quiz-add]')) { self.addToCart(); return; }
      if (t.closest('[data-ws-quiz-choose]')) { self.choose(); return; }
    });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); self.close(); return; }
      if (e.key === 'Tab') self.trapFocus(e);
    });
  };

  Quiz.prototype.cssReady = function () {
    var link = this.css;
    if (!link) return Promise.resolve();
    if (link.media !== 'all') link.media = 'all';
    if (link.sheet) return Promise.resolve();
    return new Promise(function (res) {
      var done = function () { res(); };
      link.addEventListener('load', done, { once: true });
      link.addEventListener('error', done, { once: true });
      setTimeout(done, 1500);
    });
  };

  Quiz.prototype.open = function (trigger) {
    var self = this;
    this.build();
    if (!this.root) return;
    if (this.isOpen()) return;
    this.trigger = trigger || document.activeElement;
    this.cssReady().then(function () {
      clearTimeout(self.hideTimer);
      self.root.hidden = false;
      self.prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.setAttribute('data-ws-quiz-state', 'open');
      if (self.view === 'result') self.renderResult(false); else self.show(self.step, false);
      // two frames so the sheet starts off-screen, then slides
      requestAnimationFrame(function () { requestAnimationFrame(function () {
        self.root.setAttribute('data-open', 'true');
        self.focusCurrent();
      }); });
      if (!self.started) { self.started = true; self.track('start'); }
    });
  };

  Quiz.prototype.isOpen = function () { return !!this.root && this.root.getAttribute('data-open') === 'true'; };

  Quiz.prototype.close = function () {
    if (!this.isOpen()) return;
    var self = this;
    this.root.setAttribute('data-open', 'false');
    document.documentElement.style.overflow = this.prevOverflow || '';
    document.documentElement.setAttribute('data-ws-quiz-state', 'closed');
    var t = this.trigger;
    if (t && t.focus && document.contains(t)) { try { t.focus({ preventScroll: true }); } catch (_) {} }
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(function () { if (!self.isOpen()) self.root.hidden = true; }, motion() ? 280 : 20);
  };

  Quiz.prototype.focusables = function () {
    return $$('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', this.panel)
      .filter(function (el) { return el.offsetParent !== null && !el.closest('[hidden]') && getComputedStyle(el).visibility !== 'hidden'; });
  };

  Quiz.prototype.trapFocus = function (e) {
    var list = this.focusables();
    if (!list.length) { e.preventDefault(); this.panel.focus(); return; }
    var first = list[0], last = list[list.length - 1], active = document.activeElement;
    var inside = this.panel.contains(active);
    if (e.shiftKey && (active === first || !inside || active === this.panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || !inside)) { e.preventDefault(); first.focus(); }
  };

  Quiz.prototype.focusCurrent = function () {
    var target = this.view === 'result' ? $('[data-ws-quiz-name]', this.result) : $('.ws-quiz__q', this.steps[this.step]);
    if (target) { try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); } }
    else this.panel.focus();
  };

  /* ---- steps */
  Quiz.prototype.show = function (n, focus) {
    this.view = 'step';
    this.step = Math.max(0, Math.min(this.steps.length - 1, n));
    var self = this;
    this.steps.forEach(function (s, i) {
      s.hidden = i !== self.step;
      var key = s.getAttribute('data-ws-quiz-step');
      $$('[data-ws-quiz-answer]', s).forEach(function (b) {
        b.setAttribute('aria-pressed', self.answers[key] === b.getAttribute('data-ws-quiz-answer') ? 'true' : 'false');
      });
    });
    if (this.result) this.result.hidden = true;
    this.bars.forEach(function (b, i) { if (i <= self.step) b.setAttribute('data-done', ''); else b.removeAttribute('data-done'); });
    if (this.count) this.count.textContent = 'Question ' + (this.step + 1) + ' of ' + this.steps.length;
    var key = this.steps[this.step].getAttribute('data-ws-quiz-step');
    if (this.back) { if (this.step === 0) this.back.setAttribute('data-inert', ''); else this.back.removeAttribute('data-inert'); this.back.hidden = false; }
    if (this.next) this.next.hidden = !this.answers[key];
    // the row only exists when it has something in it: Back from question 2 on, Next on a question already answered
    if (this.nav) this.nav.hidden = this.step === 0 && !this.answers[key];
    if (this.body) this.body.scrollTop = 0;
    this.root.setAttribute('data-ws-quiz-view', 'step-' + (this.step + 1));
    if (focus !== false && this.isOpen()) this.focusCurrent();
  };

  Quiz.prototype.answer = function (btn) {
    if (this.advancing) return;
    var stepEl = btn.closest('[data-ws-quiz-step]');
    var key = stepEl.getAttribute('data-ws-quiz-step');
    this.answers[key] = btn.getAttribute('data-ws-quiz-answer');
    $$('[data-ws-quiz-answer]', stepEl).forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
    var self = this;
    this.advancing = true;
    setTimeout(function () { self.advancing = false; self.forward(); }, motion() ? 180 : 0);
  };

  Quiz.prototype.forward = function () {
    var key = this.steps[this.step].getAttribute('data-ws-quiz-step');
    if (!this.answers[key]) return;
    if (this.step < this.steps.length - 1) this.show(this.step + 1);
    else this.renderResult(true);
  };

  Quiz.prototype.restart = function () {
    this.answers = {};
    this.show(0);
    this.track('restart');
  };

  /* ---- result */
  Quiz.prototype.fillFor = function (tier) {
    var fills = this.data.fills || [];
    for (var i = 0; i < fills.length; i++) if (fills[i].key === tier.key) return fills[i];
    return null;
  };

  Quiz.prototype.pdpRadio = function (fill) {
    return fill && this.pdp ? $('[data-ws-fill][value="' + fill.id + '"]', this.pdp) : null;
  };

  Quiz.prototype.renderResult = function (fresh) {
    var a = this.answers;
    for (var i = 0; i < STEPS.length; i++) if (!a[STEPS[i]]) { this.show(i); return; }
    var idx = calcTier(a);
    var tier = TIERS[idx];
    var fill = this.fillFor(tier);
    var radio = this.pdpRadio(fill);
    this.current = { tier: tier, fill: fill };
    this.view = 'result';
    var r = this.result;
    this.steps.forEach(function (s) { s.hidden = true; });
    r.hidden = false;
    this.bars.forEach(function (b) { b.setAttribute('data-done', ''); });
    if (this.count) this.count.textContent = 'Your fill';
    if (this.nav) this.nav.hidden = true; // Back / Next belong to the questions; the result has Start over
    if (this.back) this.back.hidden = true;
    if (this.next) this.next.hidden = true;

    var name = $('[data-ws-quiz-name]', r); if (name) name.textContent = (fill && fill.name) || tier.name;
    var price = $('[data-ws-quiz-price]', r); if (price) price.textContent = (fill && fill.price) || '';
    var wt = $('[data-ws-quiz-weight]', r);
    if (wt) {
      var line = (fill && fill.weight) || tier.wool; // "about 2 lb of wool" from the Fill block; a sentence here, so capitalised
      wt.textContent = line.charAt(0).toUpperCase() + line.slice(1) + '. ' + tier.detail;
    }
    var why = $('[data-ws-quiz-reason]', r); if (why) why.textContent = buildReason(a, idx);
    var note = $('[data-ws-quiz-note]', r);
    if (note) { var n = borderlineNote(a, idx); note.textContent = n; note.hidden = !n; }
    var photo = $('[data-ws-quiz-photo]', r), img = $('[data-ws-quiz-img]', r);
    var src = (fill && fill.img) || (radio && radio.dataset.img) || '';
    if (img && photo) {
      if (src) { if (img.getAttribute('src') !== src) img.src = src; img.alt = (fill && fill.alt) || (radio && radio.dataset.alt) || tier.name; photo.hidden = false; }
      else photo.hidden = true;
    }
    var add = $('[data-ws-quiz-add]', r);
    if (add) {
      var available = !!(fill && fill.available);
      add.disabled = !available;
      add.textContent = fill ? (available ? add.getAttribute('data-add-text') : add.getAttribute('data-sold-text')) : add.getAttribute('data-add-text');
      add.setAttribute('aria-label', (available ? 'Add ' : '') + ((fill && fill.name) || tier.name) + (available ? ' to your cart' : ' is sold out'));
      if (!fill) add.hidden = true;
    }
    var choose = $('[data-ws-quiz-choose]', r);
    if (choose) {
      var canChoose = this.standalone ? !!(fill && this.data.productUrl) : !!radio;
      choose.hidden = !canChoose;
      choose.setAttribute('aria-label', 'Choose ' + ((fill && fill.name) || tier.name) + (this.standalone ? ' on the pillow page' : ' on this page'));
    }
    this.root.setAttribute('data-ws-quiz-view', 'result');
    this.root.setAttribute('data-ws-quiz-pick', tier.key); // never data-ws-quiz-result: that marks the result section
    if (this.body) this.body.scrollTop = 0;
    if (fresh) this.track('complete', tier.name);
    if (this.isOpen()) this.focusCurrent();
  };

  // Select the fill in the page's chooser: the same change event a tap on the row fires, so
  // woolshire-pdp.js moves the price, the photo (cross-fade), the sticky bar and ?variant=.
  Quiz.prototype.selectInPdp = function () {
    var fill = this.current && this.current.fill;
    var radio = this.pdpRadio(fill);
    if (!radio) return null;
    if (!radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
    else radio.dispatchEvent(new Event('click'));
    return radio;
  };

  Quiz.prototype.choose = function () {
    if (!this.current || !this.current.fill) return;
    var fill = this.current.fill;
    if (this.standalone) {
      // off the product page: open the product page with this fill chosen (the PDP selects ?variant= server-side)
      if (!this.data.productUrl) return;
      this.track('choose', fill.name);
      this.close();
      window.location.href = this.data.productUrl + '?variant=' + encodeURIComponent(fill.id) + '#fill';
      return;
    }
    this.selectInPdp();
    this.track('choose', fill.name);
    this.close();
  };

  Quiz.prototype.addToCart = function () {
    var fill = this.current && this.current.fill;
    if (!fill || !fill.available) return;
    var form = this.pdp && $('[data-ws-form]', this.pdp);
    this.selectInPdp();
    if (form) {
      var idInput = $('[data-ws-variant-id]', form);
      if (idInput && String(idInput.value) !== String(fill.id)) idInput.value = fill.id;
    }
    this.track('add_to_cart', fill.name);
    this.close();
    if (form) {
      if (window.cartDrawer && window.cartDrawer.addFromForm) window.cartDrawer.addFromForm(form);
      else if (form.requestSubmit) form.requestSubmit(); // layout/theme.liquid's interceptor queues it for the drawer
      else form.submit();
    } else if (window.cartDrawer && window.cartDrawer.addFromForm) {
      // standalone: a one-line form with the recommended variant, through the drawer's own add path
      // (/cart/add.js, sections re-rendered, drawer opened, header count updated)
      var f = document.createElement('form');
      var i = document.createElement('input'); i.type = 'hidden'; i.name = 'id'; i.value = fill.id; f.appendChild(i);
      var qn = document.createElement('input'); qn.type = 'hidden'; qn.name = 'quantity'; qn.value = '1'; f.appendChild(qn);
      window.cartDrawer.addFromForm(f);
    } else {
      window.location.href = '/cart/add?id=' + encodeURIComponent(fill.id) + '&quantity=1';
    }
  };

  // Measurement hook (no new scripts): a dataLayer event and data-ws-quiz-state / data-ws-quiz-last on <html>
  // (never plain data-ws-quiz: that is the dialog root's own marker).
  Quiz.prototype.track = function (action, fill) {
    try {
      window.dataLayer = window.dataLayer || [];
      var ev = { event: 'ws_fill_quiz', quiz_action: action };
      if (fill) ev.quiz_fill = fill;
      window.dataLayer.push(ev);
      document.documentElement.setAttribute('data-ws-quiz-last', action);
    } catch (_) {}
  };

  /* ---------------------------------------------------------------- boot */
  // One dialog per page. The PDP's template wins (its result drives the page's own fill chooser and
  // form); otherwise the first standalone template. Every trigger on the page opens that one dialog,
  // and focus goes back to whichever trigger opened it.
  var quiz = null;
  function templateId() {
    var t = $('[data-ws-quiz-template]:not([data-ws-quiz-standalone])') || $('[data-ws-quiz-template]');
    return t ? t.getAttribute('data-ws-quiz-template') : null;
  }
  function instance() {
    var id = templateId();
    if (!id) return null;
    if (!quiz || (quiz.sectionId !== id && !quiz.root)) quiz = new Quiz(id);
    return quiz;
  }
  function boot() {
    if (!templateId()) return; // no template: every trigger stays a plain link to the guide
    $$('[data-ws-quiz-open]').forEach(function (link) {
      if (link.__wsQuiz) return;
      link.__wsQuiz = true;
      link.addEventListener('click', function (e) {
        var q = instance();
        if (!q) return; // follow the link
        e.preventDefault();
        q.open(link);
      });
    });
    // Small surface for the browser check (and anyone measuring): the same functions the dialog uses.
    window.wsFillQuiz = {
      open: function () { var q = instance(); if (q) q.open($('[data-ws-quiz-open]')); },
      close: function () { if (quiz) quiz.close(); },
      calc: function (a) { return TIERS[calcTier(a)].name; },
      tier: function (a) { return TIERS[calcTier(a)]; },
      reason: function (a) { var i = calcTier(a); var n = borderlineNote(a, i); return buildReason(a, i) + (n ? ' ' + n : ''); },
      tiers: TIERS
    };
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
